import { useHistory, generatePath } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { mdiAlert, mdiCheckCircleOutline, mdiPlus } from '@mdi/js'
import { unwrapResult } from '@reduxjs/toolkit'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import routes from 'app/routes'
import { useHomeRouteParams } from 'features/home/hooks'

import {
  purchaseRequestSelectors,
  activeSubscriptionSelector,
  migrateSubscriptionSelector,
  selectedHomeSelector,
  renewSubscriptionSelector,
} from 'features/home/selectors'

import { subscriptionUpdateMessages } from '../messages'

import { SectionLabel } from 'components/Typography'
import { CommonLinkButton } from 'components/Button'
import { MdiIcon } from 'components/Icons'
import {
  StyledUpdateSubscriptionWrapper,
  StyledCardsBlock,
  StyledPaymentMethods,
  MigrateSuccessIcon,
  MigrateErrorIcon,
} from './styles'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import {
  defaultPaymentMethodRequestSelectors,
  detachPaymentMethodSelectors,
  paymentMethodsListSelector,
  selectedPaymentMethodIdSelector,
} from 'features/paymentMethods/selectors'

import { LoaderOverlay } from 'components/Loader'
import PaymentMethodsCardsList from 'features/paymentMethods/components/PaymentMethodsCardsList'
import SubsManageBottomPanel from 'components/SubsManageBottomPanel'
import SuccessModal from 'components/Modal/SuccessModal'
import { useState } from 'react'
import { commonMessages } from 'app/i18n/messages'
import { isStripeManaged } from 'features/home/utils'
import { migrateSubscriptionThunk, renewSubscriptionThunk } from '../slice'

interface UpdateSubscriptionProps {
  onAddNewMethod: () => void
}

function UpgradeSubscription(props: UpdateSubscriptionProps) {
  const history = useHistory()
  const dispatch = useAppDispatch()
  const { homeId } = useHomeRouteParams()
  const { formatMessage } = useIntl()
  const currentRootId = useAppSelector(currentRootDomainIdSelector)
  const selectedPaymentMethod = useAppSelector(selectedPaymentMethodIdSelector)
  const currentHome = useAppSelector(selectedHomeSelector.data)
  const homeName = currentHome?.name || commonMessages.noName
  const [isSuccessOpen, setIsSuccessOpen] = useState(false)
  const [isErrorOpen, setIsErrorOpen] = useState(false)

  const planName = formatMessage(commonMessages.essentialPlan)

  const isPurchaseRequestLoading = useAppSelector(
    purchaseRequestSelectors.isFetching
  )
  const isDefaultPMRequestLoading = useAppSelector(
    defaultPaymentMethodRequestSelectors.isFetching
  )
  const paymentMethodsAreFetching = useAppSelector(
    paymentMethodsListSelector.isFetching
  )

  const isPaymentMethodDetaching = useAppSelector(
    detachPaymentMethodSelectors.isFetching
  )
  const isActiveSubscriptionLoading = useAppSelector(
    activeSubscriptionSelector.isFetching
  )

  const isMigratingSubscription = useAppSelector(
    migrateSubscriptionSelector.isFetching
  )

  const isFetchingRenewSubscription = useAppSelector(
    renewSubscriptionSelector.isFetching
  )

  const isDataLoading =
    isPaymentMethodDetaching ||
    isPurchaseRequestLoading ||
    paymentMethodsAreFetching ||
    isDefaultPMRequestLoading ||
    isMigratingSubscription ||
    isFetchingRenewSubscription ||
    isActiveSubscriptionLoading
  const home = useAppSelector(selectedHomeSelector.data)

  const isRenew = isStripeManaged(home)

  const handleMigrateSubscription = async () => {
    try {
      if (selectedPaymentMethod && currentRootId) {
        await dispatch(
          migrateSubscriptionThunk({
            paymentMethodId: selectedPaymentMethod,
            rootDomainId: currentRootId,
            homeId,
          })
        ).then(unwrapResult)
        setIsSuccessOpen(true)
      }
    } catch (error) {
      setIsErrorOpen(true)
      console.log('Error while migrating a subscription', error)
      // TODO: implement after the design is ready
    }
  }

  const handleRenewSubscription = async () => {
    try {
      selectedPaymentMethod &&
        currentRootId &&
        (await dispatch(
          renewSubscriptionThunk({
            paymentMethodId: selectedPaymentMethod,
            rootDomainId: currentRootId,
            homeId,
          })
        ))
      setIsSuccessOpen(true)
    } catch (error) {
      setIsErrorOpen(true)
      console.log('Error while renew a subscription', error)
    }
  }

  const handleCancel = () => {
    if (currentRootId) {
      const homeStatusPath = generatePath(routes.homeStatus, {
        homeId,
        rootDomainId: currentRootId,
      })
      history.push(homeStatusPath)
    }
  }

  const handleSuccessUpdated = () => {
    if (currentRootId) {
      const homeStatusPath = generatePath(routes.homeStatus, {
        homeId,
        rootDomainId: currentRootId,
      })
      history.push(homeStatusPath)
    }
    setIsSuccessOpen(false)
  }
  const handleCloseErrorModal = () => {
    setIsErrorOpen(false)
  }
  return (
    <StyledUpdateSubscriptionWrapper>
      <SuccessModal
        title={
          <>
            <MigrateSuccessIcon path={mdiCheckCircleOutline} size={4} />
            <br />
            {formatMessage(commonMessages.success)}
          </>
        }
        isOpen={isSuccessOpen}
        confirmBtnTitle={formatMessage(commonMessages.close)}
        onConfirm={handleSuccessUpdated}
      >
        {formatMessage(subscriptionUpdateMessages.successText, {
          planName,
          homeName,
          bold: (str) => <b>{str}</b>,
        })}
      </SuccessModal>
      <SuccessModal
        title={
          <>
            <MigrateErrorIcon path={mdiAlert} size={4} />
            <br />
            {formatMessage(subscriptionUpdateMessages.errorTitle)}
          </>
        }
        isOpen={isErrorOpen}
        confirmBtnTitle={formatMessage(commonMessages.close)}
        onConfirm={handleCloseErrorModal}
      >
        {formatMessage(subscriptionUpdateMessages.errorText)}
      </SuccessModal>
      {isDataLoading && <LoaderOverlay isSemiTransparent />}

      <StyledCardsBlock>
        <StyledPaymentMethods>
          <SectionLabel>
            {/* TODO: Localization */}
            <span>Payment Method</span>
            <CommonLinkButton onClick={props.onAddNewMethod}>
              <MdiIcon path={mdiPlus} />
            </CommonLinkButton>
          </SectionLabel>

          <PaymentMethodsCardsList />
        </StyledPaymentMethods>
      </StyledCardsBlock>
      <SubsManageBottomPanel
        isSubmitDisabled={!selectedPaymentMethod || isDataLoading}
        totalToPayAmount="$0"
        submitLabel={formatMessage(subscriptionUpdateMessages.checkoutSubmit)}
        handleSubmit={() => {
          isRenew ? handleRenewSubscription() : handleMigrateSubscription()
        }}
        handleCancel={handleCancel}
      />
    </StyledUpdateSubscriptionWrapper>
  )
}

export default UpgradeSubscription
